<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import AgenteIaAPI from 'dashboard/api/agenteIa';
import Button from 'dashboard/components-next/button/Button.vue';
import AgenteIaLayout from '../components/AgenteIaLayout.vue';

const { t } = useI18n();
const conexao = ref(null);
const bloqueio = ref('');
const trocando = ref(false);
// O comando some da tela sempre que ela abre: fica mascarado até o usuário pedir para ver.
// Copiar sempre leva o comando de verdade, mesmo com o token oculto.
const mostrarToken = ref(false);
const MASCARA = '•'.repeat(12);

const comandoExibido = computed(() => {
  if (!conexao.value) return '';
  if (mostrarToken.value) return conexao.value.comando;
  return conexao.value.comando.replaceAll(conexao.value.token, MASCARA);
});

onMounted(async () => {
  try {
    conexao.value = (await AgenteIaAPI.claude()).data;
  } catch (e) {
    const erro = e?.response?.data?.error || t('AGENTE_IA.CLAUDE.ERRO');
    if (e?.response?.status === 422) bloqueio.value = erro;
    else useAlert(erro);
  }
});

const copiar = async texto => {
  await copyTextToClipboard(texto);
  useAlert(t('AGENTE_IA.CLAUDE.COPIADO'));
};

const novoToken = async () => {
  trocando.value = true;
  try {
    conexao.value = (await AgenteIaAPI.novoTokenClaude()).data;
    useAlert(t('AGENTE_IA.CLAUDE.TOKEN_NOVO'));
  } finally {
    trocando.value = false;
  }
};
</script>

<template>
  <AgenteIaLayout
    :title="t('AGENTE_IA.CLAUDE.TITULO')"
    :description="t('AGENTE_IA.CLAUDE.DESCRICAO')"
  >
    <p
      v-if="bloqueio"
      class="p-4 text-sm rounded-xl bg-n-amber-2 text-n-amber-11 outline outline-1 outline-n-amber-6"
    >
      {{ bloqueio }}
    </p>
    <p v-else-if="!conexao" class="text-sm text-n-slate-11">
      {{ t('AGENTE_IA.CARREGANDO') }}
    </p>
    <template v-else>
      <section class="flex flex-col gap-2">
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('AGENTE_IA.CLAUDE.COMANDO') }}
        </h2>
        <pre
          class="p-3 overflow-x-auto text-xs whitespace-pre-wrap break-all rounded-lg bg-n-alpha-black2 text-n-slate-12"
          >{{ comandoExibido }}</pre>
        <div class="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            :label="
              t(
                mostrarToken
                  ? 'AGENTE_IA.CLAUDE.OCULTAR_TOKEN'
                  : 'AGENTE_IA.CLAUDE.MOSTRAR_TOKEN'
              )
            "
            @click="mostrarToken = !mostrarToken"
          />
          <Button
            size="sm"
            :label="t('AGENTE_IA.CLAUDE.COPIAR')"
            @click="copiar(conexao.comando)"
          />
          <Button
            size="sm"
            variant="outline"
            :label="t('AGENTE_IA.CLAUDE.GERAR_NOVO')"
            :is-loading="trocando"
            @click="novoToken"
          />
        </div>
        <p class="text-xs text-n-slate-11">
          {{ t('AGENTE_IA.CLAUDE.AVISO_TOKEN') }}
        </p>
      </section>
      <section class="flex flex-col gap-2">
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('AGENTE_IA.CLAUDE.CAPACIDADES') }}
        </h2>
        <ul class="flex flex-col gap-1">
          <li
            v-for="capacidade in conexao.capacidades"
            :key="capacidade.key"
            class="text-sm text-n-slate-12"
          >
            <span class="font-medium">{{ capacidade.label }}</span>
            <span class="text-n-slate-11"> — {{ capacidade.description }}</span>
          </li>
        </ul>
        <p class="text-xs text-n-slate-11">
          {{ t('AGENTE_IA.CLAUDE.DRY_RUN') }}
        </p>
      </section>
    </template>
  </AgenteIaLayout>
</template>
